import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarDays, RefreshCw, ShoppingBasket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import UserNavbar from "../../components/UserNavbar";

interface Subscription {
  _id?: string;
  id?: string;
  product_id?: string | { _id?: string; id?: string; name?: string; image?: string };
  product_name?: string;
  name?: string;
  image?: string;
  price?: number;
  mrp_price?: number;
  quantity?: number;
  quantity_added?: number;
  subscription_plan?: string;
  plan?: string;
  product_description?: string;
  weight?: string;
  qty?: string;
}

const PROD_API_BASE = "https://www.frendrops.in/api";
const apiUrl = (path: string) => `${PROD_API_BASE}/${path.replace(/^\//, "")}`;

const getProduct = (item: Subscription) =>
  item.product_id && typeof item.product_id === "object" ? item.product_id : undefined;

const getProductId = (item: Subscription) => {
  const product = getProduct(item);
  return String(product?._id || product?.id || item.product_id || item._id || item.id || "");
};

export default function CustomerSubscriptions() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const fetchSubscriptions = useCallback(async (refresh = false) => {
    try {
      refresh ? setRefreshing(true) : setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      if (!token || token === "null" || token === "undefined") {
        navigate("/login", { state: { from: { pathname: "/subscription" } } });
        return;
      }
      const { data } = await axios.get(apiUrl("subscriptions"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.subscriptions)
        ? data.subscriptions
        : Array.isArray(data)
        ? data
        : [];
      setSubscriptions(items);
    } catch (err: any) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate("/login", { state: { from: { pathname: "/subscription" } } });
        return;
      }
      setSubscriptions([]);
      setError(err?.response?.data?.message || "Failed to load subscriptions");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const monthlyTotal = useMemo(
    () =>
      subscriptions.reduce(
        (sum, item) =>
          sum + (Number(item.price) || 0) * (Number(item.quantity ?? item.quantity_added) || 1) * 30,
        0
      ),
    [subscriptions]
  );

  return (
    <div className="min-h-screen bg-[#f8f7ff]">
      <UserNavbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-40 pb-24">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Subscriptions</h1>
            <p className="text-gray-500 mt-1">Manage products scheduled for recurring delivery.</p>
          </div>
          <div className="flex items-center gap-3">
            {subscriptions.length > 0 && (
              <span className="rounded-full bg-violet-700 px-4 py-2 text-sm font-bold text-white">
                {subscriptions.length} active
              </span>
            )}
            <button
              type="button"
              onClick={() => fetchSubscriptions(true)}
              disabled={refreshing}
              className="p-2.5 rounded-xl bg-white border border-violet-100 text-violet-700 disabled:opacity-50"
              aria-label="Refresh subscriptions"
            >
              <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
            <RefreshCw className="mx-auto animate-spin text-violet-700" size={34} />
            <p className="mt-4 text-gray-500">Loading subscriptions...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl p-10 text-center shadow-sm">
            <p className="text-red-600">{error}</p>
            <button onClick={() => fetchSubscriptions()} className="mt-5 bg-violet-700 text-white px-5 py-3 rounded-xl font-semibold">
              Try Again
            </button>
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 sm:p-16 text-center shadow-sm">
            <CalendarDays size={68} className="mx-auto text-violet-200" />
            <h2 className="text-xl font-bold mt-5">No Subscriptions Yet</h2>
            <p className="text-gray-500 max-w-xl mx-auto mt-2">
              Subscribe to your favourite products and get them delivered daily, alternate days, or on custom dates every month.
            </p>
            <button
              onClick={() => navigate("/productList")}
              className="mt-7 inline-flex items-center gap-2 bg-violet-700 hover:bg-violet-800 text-white px-6 py-3 rounded-xl font-bold"
            >
              <ShoppingBasket size={19} /> Browse Products
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {subscriptions.map((item, index) => {
                const product = getProduct(item);
                const productId = getProductId(item);
                const name = item.product_name || item.name || product?.name || "Product";
                const image = item.image || product?.image;
                const qty = Number(item.quantity ?? item.quantity_added) || 1;
                const plan = item.subscription_plan || item.plan;

                return (
                  <button
                    type="button"
                    key={item._id || item.id || `${productId}-${index}`}
                    onClick={() => productId && navigate(`/product/${productId}`)}
                    className="w-full text-left bg-white rounded-2xl border-2 border-violet-200 overflow-hidden shadow-sm hover:shadow-md transition"
                  >
                    <div className="p-4 flex gap-4 items-center bg-violet-50/60">
                      {image ? (
                        <img src={image} alt={name} className="w-20 h-20 rounded-xl object-cover bg-white" />
                      ) : (
                        <div className="w-20 h-20 rounded-xl bg-violet-100 flex items-center justify-center">
                          <CalendarDays className="text-violet-400" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="font-bold text-gray-900">{name}</h2>
                          <span className="text-xs font-bold bg-violet-700 text-white px-2 py-1 rounded-full">Subscribed</span>
                        </div>
                        {(item.weight || item.qty) && <p className="text-xs text-gray-500 mt-1">{item.weight || item.qty}</p>}
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <span className="text-xs font-semibold text-violet-700 bg-violet-100 px-2 py-1 rounded-lg">
                            {plan ? `${plan} Plan` : "Subscription"}
                          </span>
                          <span className="text-xs text-gray-500">Qty: {qty}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          {Number(item.mrp_price) > 0 && (
                            <span className="text-xs text-gray-400 line-through">₹{item.mrp_price}</span>
                          )}
                          <span className="text-sm font-bold text-violet-700">₹{Number(item.price) || 0} / delivery</span>
                        </div>
                        {item.product_description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.product_description}</p>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-5 bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Estimated monthly total</span>
                <strong className="text-violet-700">₹{monthlyTotal.toFixed(0)}</strong>
              </div>
              <div className="flex justify-between gap-4 mt-2">
                <span className="text-gray-500">Active subscriptions</span>
                <strong>{subscriptions.length}</strong>
              </div>
            </div>

            <button
              onClick={() => navigate("/productList")}
              className="mt-5 w-full flex items-center justify-center gap-2 bg-violet-700 hover:bg-violet-800 text-white py-4 rounded-xl font-bold"
            >
              <ShoppingBasket size={19} /> Add More Products
            </button>
          </>
        )}
      </main>
    </div>
  );
}
